import {makeObservable, observable, runInAction} from 'mobx';

import type {CoreOrB2b} from '../../utils/coreOrB2b/types';
import type {Combine, OmitRecursively} from '../../utils/utility-types';
import type {
  BNCoachConferencesCreateParams,
  BNCoachConferencesStartArchiveParams,
  BNCoachConferencesStopParams,
} from '../types/bnrks/methods';
import type {
  CoreClientConferencesCreateParams,
  CoreClientConferencesDeleteParams,
  CoreClientConferencesJoinParams,
  CoreClientConferencesReadParams,
  CoreCoachConferencesCreateParams,
  CoreCoachConferencesDeleteParams,
  CoreCoachConferencesJoinParams,
  CoreCoachConferencesReadParams,
  CoreCoachConferencesShareArchivesParams,
  CoreCoachConferencesStartArchiveParams,
  CoreCoachConferencesStartParams,
  CoreCoachConferencesStopArchiveParams,
  CoreCoachConferencesStopParams,
  CoreCoachConferencesTakeParams,
  CoreCoachConferencesUpdateParams,
} from '../types/core/methods';
import type {MethodStatus} from '../utils/entityStore';
import type {ConferenceApiModel} from './types';

import {pickCoreOrB2b} from '../../utils/coreOrB2b/pickCoreOrB2b';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';

class ClientConferenceStore<
  T extends ConferenceApiModel = ConferenceApiModel,
> extends EntityStore<
  T,
  CoreClientConferencesReadParams,
  CoreClientConferencesCreateParams,
  object,
  CoreClientConferencesDeleteParams
> {
  async join(
    conference: T,
    params?: Omit<CoreClientConferencesJoinParams, '_id'>,
  ) {
    const result = await apiRequest<CoreClientConferencesJoinParams>({
      method: 'client.conferences.join',
      params: {
        ...params,
        _id: conference._id,
      },
    });

    const _conference = this._processResult(result, params);

    return {
      conference: _conference,
      token: result.token as string,
    };
  }
}

class CoachConferenceStore<
  T extends ConferenceApiModel = ConferenceApiModel,
> extends EntityStore<
  T,
  CoreCoachConferencesReadParams,
  Combine<CoreCoachConferencesCreateParams, BNCoachConferencesCreateParams>,
  CoreCoachConferencesUpdateParams,
  CoreCoachConferencesDeleteParams
> {
  shareArchive = this._createMethod<CoreCoachConferencesShareArchivesParams>({
    method: 'coach.conferences.share_archives',
  });
  starting: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  stopping: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  stop = this._createMethod<
    BNCoachConferencesStopParams | CoreCoachConferencesStopParams
  >({
    method: 'coach.conferences.stop',
    onBefore: entity => {
      runInAction(() => {
        this.stopping = {
          entity,
          error: null,
          inProgress: true,
          success: false,
        };
      });
    },
    onFail: (entity, error) => {
      runInAction(() => {
        this.stopping = {
          entity,
          error,
          inProgress: false,
          success: false,
        };
      });
    },
    onSuccess: entity => {
      runInAction(() => {
        this.stopping = {
          entity,
          error: null,
          inProgress: false,
          success: true,
        };
      });
    },
  });
  stopArchive = this._createMethod<CoreCoachConferencesStopArchiveParams>({
    method: 'coach.conferences.stop_archive',
  });
  private internalStartArchive = this._createMethod<
    | BNCoachConferencesStartArchiveParams
    | CoreCoachConferencesStartArchiveParams
  >({
    method: 'coach.conferences.start_archive',
  });

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      starting: observable,
      stopping: observable,
    });
  }

  async join(
    conference: T,
    params?: Omit<CoreCoachConferencesJoinParams, '_id'>,
  ) {
    const result = await apiRequest<CoreCoachConferencesJoinParams>({
      method: 'coach.conferences.join',
      params: {
        ...params,
        _id: conference._id,
      },
    });

    const _conference = this._processResult(result, params);

    return {
      conference: _conference,
      token: result.token as string,
    };
  }

  async start(
    conference: T,
    params?: Omit<CoreCoachConferencesStartParams, '_id'>,
  ) {
    try {
      runInAction(() => {
        this.starting = {
          entity: conference,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const result = await apiRequest<CoreCoachConferencesStartParams>({
        method: 'coach.conferences.start',
        params: {
          ...params,
          _id: conference._id,
        },
      });

      const _conference = this._processResult(result, params);

      runInAction(() => {
        this.starting = {
          entity: _conference,
          error: null,
          inProgress: false,
          success: true,
        };
      });

      return {
        conference: _conference,
        token: result.token as string,
      };
    } catch (error) {
      runInAction(() => {
        this.starting = {
          entity: null,
          error,
          inProgress: false,
          success: false,
        };
      });

      throw error;
    }
  }

  startArchive(
    conference: T,
    {
      b2b,
      core,
    }: OmitRecursively<
      CoreOrB2b<
        CoreCoachConferencesStartArchiveParams,
        BNCoachConferencesStartArchiveParams
      >,
      '_id'
    > = {},
  ) {
    const params = pickCoreOrB2b<
      Omit<CoreCoachConferencesStartArchiveParams, '_id'>,
      Omit<BNCoachConferencesStartArchiveParams, '_id'>
    >({
      b2b,
      core,
    });

    return this.internalStartArchive(conference, params);
  }

  async take(
    conference: T,
    params?: Omit<CoreCoachConferencesTakeParams, '_id'>,
  ) {
    const result = await apiRequest<CoreCoachConferencesTakeParams>({
      method: 'coach.conferences.take',
      params: {
        ...params,
        _id: conference._id,
      },
    });

    const _conference = this._processResult(result, params);

    return {
      conference: _conference,
    };
  }
}

class ConferenceStore<
  T extends ConferenceApiModel = ConferenceApiModel,
> extends EntityStore<T> {
  client = new ClientConferenceStore<T>('conference', {
    create: 'client.conferences.create',
    delete: 'client.conferences.delete',
    fetch: 'client.conferences.read',
  });
  coach = new CoachConferenceStore<T>('conference', {
    create: 'coach.conferences.create',
    delete: 'coach.conferences.delete',
    fetch: 'coach.conferences.read',
    update: 'coach.conferences.update',
  });
}

export const conferenceStore = new ConferenceStore('conference', {});
